import React from "react";
import { motion } from "framer-motion";
import { useLang } from "../../context/LanguageContext";
import { Truck, Network, Radar, Check } from "lucide-react";

const REAL_IMG_1 =
  "https://customer-assets.emergentagent.com/job_lklog-partner/artifacts/8t3kgmca_image.png";
const REAL_IMG_2 =
  "https://customer-assets.emergentagent.com/job_lklog-partner/artifacts/58vcdp68_image.png";

export default function Capabilities() {
  const { t, lang } = useLang();

  const fleetBullets = [
    t("capabilities.c1_b1"),
    t("capabilities.c1_b2"),
    t("capabilities.c1_b3"),
    t("capabilities.c1_b4"),
  ];

  const gallery = [
    {
      src: REAL_IMG_1,
      caption:
        lang === "pl"
          ? "Kręgi stalowe — transport w naczepie mulda"
          : "Steel coils — transport in a tipper trailer",
    },
    {
      src: REAL_IMG_2,
      caption:
        lang === "pl"
          ? "Mocowanie ładunku — zabezpieczenie kręgów stalowych"
          : "Cargo securing — steel coils lashing",
    },
  ];

  return (
    <section
      id="capabilities"
      data-testid="capabilities-section"
      className="relative py-24 md:py-32 bg-[#0D0D0F]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-16 max-w-3xl">
          <div className="flex items-center gap-3 mb-6">
            <div className="gold-divider" />
            <span className="label-gold">{t("capabilities.label")}</span>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-white leading-[1.1] tracking-tight mb-6">
            {t("capabilities.title")}
          </h2>
          <p className="text-neutral-400 leading-relaxed text-base lg:text-lg">
            {t("capabilities.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Large card: Own fleet with real image */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            data-testid="capability-card-fleet"
            className="lg:col-span-7 lg:row-span-2 relative overflow-hidden rounded-sm border border-white/10 bg-[#161618] group"
          >
            <div className="absolute inset-0 opacity-30 group-hover:opacity-45 transition-opacity">
              <img src={REAL_IMG_1} alt="Fleet" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#0A0A0A] via-[#0A0A0A]/85 to-transparent" />
            </div>
            <div className="relative p-8 lg:p-12 h-full flex flex-col justify-between min-h-[460px]">
              <Truck className="w-10 h-10 text-[#F2B705]" strokeWidth={1.3} />
              <div>
                <h3 className="font-display text-2xl lg:text-3xl font-semibold text-white mb-6">
                  {t("capabilities.c1_t")}
                </h3>
                <ul className="space-y-3">
                  {fleetBullets.map((b, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm lg:text-base text-neutral-200">
                      <Check className="w-4 h-4 text-[#F2B705] mt-1 shrink-0" strokeWidth={2.5} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            data-testid="capability-card-network"
            className="lg:col-span-5 p-8 lg:p-10 bg-[#161618] border border-white/10 rounded-sm hover:border-[#F2B705]/40 transition-colors"
          >
            <Network className="w-8 h-8 text-[#F2B705] mb-6" strokeWidth={1.3} />
            <h3 className="font-display text-xl lg:text-2xl font-semibold text-white mb-4">
              {t("capabilities.c2_t")}
            </h3>
            <p className="text-sm lg:text-base text-neutral-400 leading-relaxed">
              {t("capabilities.c2_d")}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            data-testid="capability-card-ops"
            className="lg:col-span-5 p-8 lg:p-10 bg-[#161618] border border-white/10 rounded-sm hover:border-[#F2B705]/40 transition-colors"
          >
            <Radar className="w-8 h-8 text-[#F2B705] mb-6" strokeWidth={1.3} />
            <h3 className="font-display text-xl lg:text-2xl font-semibold text-white mb-4">
              {t("capabilities.c3_t")}
            </h3>
            <p className="text-sm lg:text-base text-neutral-400 leading-relaxed">
              {t("capabilities.c3_d")}
            </p>
          </motion.div>
        </div>

        {/* Realizations gallery */}
        <div className="mt-16">
          <div className="flex items-center gap-3 mb-8">
            <div className="gold-divider" />
            <span className="label-gold">
              {lang === "pl" ? "Realizacje" : "Case photos"}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {gallery.map((g, i) => (
              <motion.figure
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                data-testid={`capability-gallery-${i}`}
                className="group relative overflow-hidden rounded-sm border border-white/10"
              >
                <img
                  src={g.src}
                  alt={g.caption}
                  className="w-full aspect-[4/3] object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/20 to-transparent pointer-events-none" />
                <figcaption className="absolute bottom-0 left-0 right-0 p-5 text-sm text-white font-medium">
                  <span className="inline-block bg-[#F2B705] text-[#0A0A0A] text-[10px] tracking-widest uppercase font-bold px-2 py-1 rounded-sm mr-2">
                    LK LOG
                  </span>
                  {g.caption}
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
